import React, { useState } from "react";
import { toast } from "react-toastify";

import DashBoardSidebar from "../../layouts/dashBoard-Sidebar";
import DashboardNavbar from "../../layouts/dashboardNavbar";

import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import { AddStaff } from "../../api/server";
import { ThreeDots } from "react-loader-spinner";
import "./set-up-user-account.css";

const initialValues = {
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    agency_id: "",
    agency_user_id: "",
    trustee_password: "",
};

const validationSchema = Yup.object().shape({
    firstname: Yup.string().required("First name is required"),
    lastname: Yup.string().required("Last name is required"),
    phone: Yup.string().required("Phone number is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    username: Yup.string().required("Username is required"),
    password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase character"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
    agency_id: Yup.string().required("Agency ID is required"),
    agency_user_id: Yup.string().required("Agency User ID is required"),
    trustee_password: Yup.string()
        .required("Trustee password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase character")
    
    // Yup.string().required("Trustee password is required"),
});

const staffSections = [
    {
        id: "staff-basic", title: "Basic Info", icon: "fa-user",
        fields: [
            { name: "firstname", label: "First Name", autoComplete: "given-name" },
            { name: "lastname", label: "Last Name", autoComplete: "family-name" },
            { name: "phone", label: "Phone Number", type: "tel", autoComplete: "tel" },
            { name: "email", label: "Email", type: "email", autoComplete: "email", column: "col-lg-6" },
        ],
    },
    {
        id: "staff-account", title: "Account Info", icon: "fa-lock",
        fields: [
            { name: "username", label: "Username", autoComplete: "off" },
            { name: "password", label: "Password", type: "password" },
            { name: "confirmPassword", label: "Confirm Password", type: "password" },
        ],
    },
    {
        id: "staff-trustee", title: "US Trustee", icon: "fa-building-columns",
        fields: [
            { name: "agency_id", label: "Agency ID", autoComplete: "off" },
            { name: "agency_user_id", label: "Agency User ID", autoComplete: "off" },
            { name: "trustee_password", label: "Password", type: "password" },
        ],
    },
];

const StaffField = ({ name, label, type = "text", autoComplete, column = "col-lg-4" }) => {
    const [visible, setVisible] = useState(false);
    const password = type === "password";
    const input = <Field id={`setup-staff-${name}`} name={name}
        type={password && visible ? "text" : type} className="form-control"
        autoComplete={autoComplete || (password ? "new-password" : undefined)}
        aria-describedby={`setup-staff-${name}-error`} />;
    return (
        <div className={`${column} col-md-6`}>
            <div className="input_wrap">
                <label className="label-title" htmlFor={`setup-staff-${name}`}>{label}</label>
                {password ? <div className="setup-password-control">
                    {input}
                    <button type="button" className="password-toggle"
                        aria-label={`${visible ? "Hide" : "Show"} ${name === "trustee_password" ? "trustee password" : label.toLowerCase()}`}
                        aria-pressed={visible} onClick={() => setVisible((value) => !value)}>
                        <i className={`fa-regular ${visible ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true"></i>
                    </button>
                </div> : input}
                <ErrorMessage name={name} component="div" id={`setup-staff-${name}-error`} className="error text-danger" />
            </div>
        </div>
    );
};

const ManageStaff = () => {
    const [loading, setLoading] = useState(false);
    const handleSubmit = async (values, { resetForm }) => {
        setLoading(true);
        try {
            const res = await AddStaff(values);
            if (res?.data?.success) {
                toast.success(res.data.message);
                resetForm();
            }
        } catch (err) {
            toast.error(err.response?.data?.error || "Unable to create staff account. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="wrapper">
            <DashBoardSidebar />
            <div id="content" className="setup-user-page">
                <DashboardNavbar />
                <header className="setup-user-heading">
                    <h1>Setup Staff</h1>
                    <span className="setup-user-heading-icon" aria-hidden="true">
                        <i className="fa-solid fa-user-plus"></i>
                    </span>
                </header>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                    <Form className="setup-user-form" noValidate>
                        {staffSections.map(({ id, title, icon, fields }) => (
                            <section className="setup-user-card" aria-labelledby={id} key={id}>
                                <div className="setup-section-heading">
                                    <span className="setup-section-icon" aria-hidden="true"><i className={`fa-solid ${icon}`}></i></span>
                                    <h2 id={id}>{title}</h2>
                                </div>
                                <div className="row">
                                    {fields.map((field) => <StaffField key={field.name} {...field} />)}
                                </div>
                            </section>
                        ))}
                        <div className="setup-user-actions">
                            <button type="submit" className="btn_brnd" disabled={loading}>
                                {loading ? <ThreeDots height="20" width="65" radius="9" color="#fff"
                                    ariaLabel="Creating staff account" wrapperClassName="spinner" visible /> : "Submit"}
                            </button>
                        </div>
                    </Form>
                </Formik>
            </div>
        </div>
    );
};

export default ManageStaff;
